import { BaseAPI } from '../base.api';
import VeiculoTransportadora from '@/models/veiculoTransportadora';
import { VUE_APP_SIT_PEDIDOS_API_URL } from '@/commons/config';
import { AxiosObservable } from 'axios-observable/dist/axios-observable.interface';
import { Pedido, PagePedidoInput, CreateInclusaoVeiculoPayload } from '@/models/sit-pedido/pedido';
import { InputCreatePedido, DocumentoAnexo } from '@/store/modules/renovacao-crlv';
import { Pagination } from '@/models/sit-pedido/pagination';
import { print } from 'graphql';
import Documento from '@/models/documento';
import ShippingCompanyContactPayload from '@/models/sit-pedido/shipping_company_contact.payload';
import ShippingCompanyCreatePayload from '@/models/sit-pedido/shipping_company_create.payload';

export interface InputUpoloadDocumento {
  file: File;
  tipoDocumento: string;
  dataEmissao?: string;
  dataValidade?: string;
  orgaoExpedidor?: string;
  numeroComplemento?: string;
  horaVistoria?: string;
  idMunicipio?: number;
}

export interface PedidoInfo {
  id: number;
  nome: string;
  nomeReduzido: string;
  cpf: string;
  idCooperado: number;
  areaAtuacaoId: number;
  rg: string;
  cnhNumero: string;
  cnhValidade: string;
  logradouro: string;
  cep: string;
  dataNascimento: string;
  sexo: string;
  telefone: string;
  logradouroComplemento: string;
  logradouroNumero: string;
  pedidoId: number;
  imgUrl: string;
  aproveitarVeiculo: boolean;
}

export interface PedidoSubstituido {
  nome: string;
  cpf: string;
  rg: string;
  dataNascimento: string;
  sexo: string;
  telefone: string;
  cep: string;
  logradouro: string;
  cnhNumero: string;
  cnhValidade: string;
  idCooperado: number;
  placa: string;
  imgUrl: string;
}

export interface PedidoInfoResponse {
  cooperadoEntrante: PedidoInfo;
  cooperadoSubstituido: PedidoSubstituido;
}

const pedidoPage = require('@/query/sit-pedido/pedido/Page.graphql');
const pedidoShow = require('@/query/sit-pedido/pedido/Show.graphql');

export class PedidoAPI extends BaseAPI<Pedido> {
  private apiUrlCooperado = `${VUE_APP_SIT_PEDIDOS_API_URL}/api/cooperado`;

  constructor() {
    super();
    this.apiUrl = `${VUE_APP_SIT_PEDIDOS_API_URL}/api/pedidos`;
    this.graphqlUrl = `${VUE_APP_SIT_PEDIDOS_API_URL}/graphql`;
  }

  public createPedido(body: InputCreatePedido): AxiosObservable<Pedido> {
    return this.axios.post(`${this.apiUrl}`, { ...body });
  }

  public getPedidoInfo(pedidoId: number): AxiosObservable<PedidoInfoResponse> {
    return this.axios.post(`${this.apiUrlCooperado}/pedido`, {
      id: pedidoId,
    });
  }

  public createPedidoCompany(body: any): AxiosObservable<Pedido> {
    return this.axios.post(`${this.apiUrl}/renovacao-cadastro`, { ...body });
  }

  public uploadDocumento(
    pedidoID: number,
    {
      file,
      tipoDocumento,
      dataEmissao,
      dataValidade,
      numeroComplemento,
      orgaoExpedidor,
      horaVistoria,
      idMunicipio,
    }: InputUpoloadDocumento
  ): AxiosObservable<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('tipoDocumento', tipoDocumento);
    formData.append('dataEmissao', dataEmissao);
    formData.append('dataValidade', dataValidade);
    formData.append('numeroComplemento', numeroComplemento);
    formData.append('orgaoExpedidor', orgaoExpedidor);
    formData.append('horaVistoria', horaVistoria ? horaVistoria : '');
    formData.append('idMunicipio', idMunicipio ? String(idMunicipio) : '');

    return this.axios.post(`${this.apiUrl}/${pedidoID}/upload-arquivo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  public page(input: PagePedidoInput): AxiosObservable<Pagination<Pedido>> {
    return this.axios.post(`${this.graphqlUrl}`, {
      query: print(pedidoPage),
      variables: { ...input },
    });
  }

  public show(id: number): AxiosObservable<Pedido> {
    return this.axios.post(`${this.graphqlUrl}`, {
      query: print(pedidoShow),
      variables: { id },
    });
  }

  public download(body: DocumentoAnexo): AxiosObservable<any> {
    return this.axios.post(`${this.apiUrl}/download-arquivo`, body, {
      params: { attachment: true },
      responseType: 'blob',
    });
  }

  public createInclusaoVeiculo(data: CreateInclusaoVeiculoPayload): AxiosObservable<any> {
    return this.axios.post(`${this.apiUrl}/inclusao-veiculo`, data);
  }

  public createShippingCompanyContact(data: ShippingCompanyContactPayload): AxiosObservable<any> {
    return this.axios.post(`${this.apiUrl}/transportadora/email-codigo`, data);
  }

  public createShippingCompany(data: ShippingCompanyCreatePayload): AxiosObservable<any> {
    return this.axios.post(`${this.apiUrl}/transportadora/cadastro-transportadora`, data);
  }

  public atuationAreasList(): AxiosObservable<any> {
    return this.axios.post(`${this.apiUrl}/transportadora/list-area-atuacao`);
  }

  public servicesList(): AxiosObservable<any> {
    return this.axios.post(`${this.apiUrl}/transportadora/list-servico`);
  }

  public getFabricantes(): AxiosObservable<any> {
    return this.axios.post(`${this.apiUrl}/list-fabricante`);
  }

  public getCarroceria(id: number): AxiosObservable<any> {
    return this.axios.post(`${this.apiUrl}/${id}/list-modeloCarroceria`);
  }

  /// endpoints para substituição de cooperado

  public searchCooperado(cpf: string): AxiosObservable<Pagination<Pedido>> {
    return this.axios.get(`${this.apiUrlCooperado}/dadosHabilitacao?cpf=${cpf}`);
  }

  public listCooperadosTranspotadora(transportadoraId: number): AxiosObservable<any> {
    return this.axios.get(`${this.apiUrlCooperado}/cooperados-transportadora`, {
      params: { transportadoraId },
    });
  }


  public getPermiteAproveitar(veiculoTransportadoraId: number): AxiosObservable<any> {
    return this.axios.get(`${this.apiUrlCooperado}/permite-aproveitar`, {
      params: { veiculoTransportadoraId },
    });
  }

  public substituirCooperado(body: Object): AxiosObservable<any> {
    return this.axios.post(`${this.apiUrl}/substituicao-cooperado`, {
      ...body,
    });
  }

  public searchEnderecoCEP(cep: string): AxiosObservable<any> {
    return this.axios.get(`${this.apiUrl}/endereco`, { params: { cep } });
  }

  public uploadDocumentoSubstituicaoCooperado(
    numeroPedido: number,
    file: File,
    key?: string
  ): AxiosObservable<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('tipoDocumento', key ? key : '');

    return this.axios.post(`${this.apiUrl}/${numeroPedido}/upload-arquivo`, formData, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  public listAreasAtuacao(idTransportadora: number): AxiosObservable<any> {
    return this.axios.post(`${this.apiUrl}/listAreaByTransportadora`, { id: idTransportadora });
  }
}
